import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { pin, Fly } from "./MapView";

export const BUBBLE_ZOOM = 11;
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const bubble = (n, town) => L.divIcon({ className: "", html: `<span class="mhh-bubble">${n}<small>${esc(town)}</small></span>`, iconSize: [0, 0] });

export function groupByTown(items) {
  const g = {};
  items.filter((j) => j.lat).forEach((j) => {
    const k = j.town || j.postcode_area || "Nearby";
    (g[k] ||= { town: k, jobs: [] }).jobs.push(j);
  });
  return Object.values(g).map((t) => ({ ...t, lat: t.jobs.reduce((s, j) => s + j.lat, 0) / t.jobs.length, lng: t.jobs.reduce((s, j) => s + j.lng, 0) / t.jobs.length }));
}

function Zoom({ onZoom }) {
  const map = useMapEvents({ zoomend: (e) => onZoom(e.target.getZoom()) });
  useEffect(() => {
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(map.getContainer());
    return () => ro.disconnect();
  }, [map]);
  return null;
}

function UserMove({ onMove }) {
  const user = useRef(false);
  const cb = useRef(onMove);
  cb.current = onMove;
  const map = useMapEvents({ moveend: (e) => { if (user.current) { const b = e.target.getBounds(); cb.current?.([b.getSouth(), b.getWest(), b.getNorth(), b.getEast()].map((x) => x.toFixed(4)).join(",")); } } });
  useEffect(() => {
    const el = map.getContainer();
    const on = () => { user.current = true; };
    el.addEventListener("pointerdown", on);
    el.addEventListener("wheel", on, { passive: true });
    return () => { el.removeEventListener("pointerdown", on); el.removeEventListener("wheel", on); };
  }, [map]);
  return null;
}

function TownBubble({ t, noun }) {
  const map = useMapEvents({});
  return <Marker position={[t.lat, t.lng]} icon={bubble(t.jobs.length, t.town)} eventHandlers={{ click: () => map.flyTo([t.lat, t.lng], BUBBLE_ZOOM + 1, { duration: 0.6 }) }} title={`${t.jobs.length} ${noun} in ${t.town}`} />;
}

export default function JobsMap({ items = [], center, zoom = 8, activeId, onSelect, onMove, testid = "jobs-map", linkLabel = "View job", noun = "open jobs" }) {
  const [z, setZ] = useState(zoom);
  const towns = z < BUBBLE_ZOOM ? groupByTown(items) : null;
  return (
    <div className="h-full rounded-2xl overflow-hidden border border-slate-200" data-testid={testid} data-mode={towns ? "towns" : "pins"}>
      <MapContainer center={center} zoom={zoom} scrollWheelZoom className="h-full w-full">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <Fly center={center} zoom={zoom} />
        <UserMove onMove={onMove} />
        <Zoom onZoom={setZ} />
        {towns ? towns.map((t) => <TownBubble key={t.town} t={t} noun={noun} />) : items.filter((j) => j.lat).map((j) => (
          <Marker key={j.id} position={[j.lat, j.lng]} icon={pin(j.business_name?.slice(0, 12) || "•", activeId === j.id ? "active" : "")} eventHandlers={{ click: () => onSelect?.(j.id) }}>
            <Popup><div className="text-sm" data-testid={`map-popup-${j.id}`}><b>{j.business_name}</b><br />{j.price && <><span className="font-semibold text-[#374B5C]" data-testid="map-popup-price">{j.price}</span>{j.fair && <span className="ml-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold px-1.5 py-0.5" data-testid="map-popup-fair">Fair price</span>}<br /></>}{j.town}{j.distance != null && ` · ${j.distance} mi`}<br />
              <Link to={j.href} className="text-[#FF8166] font-semibold">{linkLabel} →</Link>
              {j.book && <Link to={j.book.href} className="mt-2 flex items-center justify-center rounded-full bg-[#FF8166] !text-white font-semibold text-xs px-3 py-1.5 hover:bg-[#f06d51] transition-colors" data-testid={`map-popup-book-${j.id}`}>{j.book.label}</Link>}</div></Popup>
          </Marker>))}
      </MapContainer>
    </div>
  );
}
